import {
  ActionIcon,
  Box,
  Center,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { deleteBrandById, getBrands } from "api/brand.service";
import { safeApiCall } from "api/common.service";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import BrandModal from "components/products/BrandModal";
import React, { useEffect, useMemo, useState } from "react";
import { Brand, Brands } from "types/model.types";

const ManageBrandPage: React.FC = () => {
  const [brands, setBrands] = useState<Brands>([]);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [page, setPage] = useState(1);
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [activePage, setActivePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredBrands = useMemo(() => {
    return brands.filter((b) =>
      b.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [brands, searchTerm]);

  const paginatedBrands = useMemo(() => {
    const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
    return filteredBrands.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredBrands, activePage]);

  // Fetch brands when the component mounts
  useEffect(() => {
    (async () => {
      try {
        const response = await safeApiCall(getBrands());
        setBrands(response?.data ?? []);
      } catch (error) {
        console.error("Failed to fetch brands:", error);
      }
    })();
  }, [reloadTrigger]);

  const rows = paginatedBrands.map((brand: Brand) => (
    <Table.Tr key={brand.id}>
      <Table.Td>{brand.name}</Table.Td>
      <Table.Td>{brand.total}</Table.Td>
      <Table.Td>
        <ActionIcon
          onClick={() => {
            setEditingBrand(brand);
            setModalOpened(true);
          }}
        >
          <IconPencil size={18} />
        </ActionIcon>
      </Table.Td>
      <Table.Td>
        <ActionIcon
          color="red"
          onClick={async () => {
            await safeApiCall(deleteBrandById(brand.id));
            setBrands((prev) => prev.filter((b) => b.id !== brand.id));
          }}
        >
          <IconTrash size={18} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
  };

  return (
    <Box>
      <Stack>
        <Title order={2}>Manage Product Brands</Title>
        <Text size="sm" c="dimmed">
          Showing 30 items per page
        </Text>
        <TextInput
          placeholder="Search by any title"
          mb="md"
          leftSection={<IconSearch size={16} stroke={1.5} />}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Table
          striped
          highlightOnHover
          withTableBorder
          style={{ minWidth: 740 }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th colSpan={4}>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text>Add New Brand</Text>
                  <ActionIcon
                    variant="filled"
                    color="blue"
                    onClick={() => {
                      setEditingBrand({ id: 0, name: "", total: 0 });
                      setModalOpened(true);
                    }}
                  >
                    <IconPlus size={16} />
                  </ActionIcon>
                </Box>
              </Table.Th>
            </Table.Tr>
            <Table.Tr>
              <Table.Th style={{ width: "75%" }}>Title</Table.Th>
              <Table.Th>Total</Table.Th>
              <Table.Th>Edit</Table.Th>
              <Table.Th>Delete</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Center>
                  <Pagination
                    total={Math.ceil(brands.length / DEFAULT_PAGE_SIZE)}
                    value={activePage}
                    onChange={setActivePage}
                    mt="md"
                  />
                </Center>
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
        <BrandModal
          opened={modalOpened}
          onClose={() => {
            setModalOpened(false);
            setEditingBrand(null);
          }}
          forceReload={() => {
            setReloadTrigger(new Date());
          }}
          brand={editingBrand}
          existingBrands={brands}
          setBrands={setBrands}
        />
      </Stack>
    </Box>
  );
};

export default ManageBrandPage;
